import Image from '@components/Image';
import { ListItemMoreButton } from '@components/ListItemMoreButton';
import { sendWarning } from '@components/Noti';
import { TipWrapper } from '@components/TipWrapper';
import IconDuplicate from '@components/icons/IconDuplicate';
import IconLocked from '@components/icons/IconLocked';
import getFeatName from '@modules/hotspot/SidebarHotspot/getFeatName';
import { $curScene, $sceneMapApi } from '@stores/dataStores';
import { $editingComponentLayerApi } from '@stores/editingComponentLayerStores';
import { type ComponentLayer, type GeneralID, ImageType } from '@types';
import { SortableElement } from 'react-sortable-hoc';
import { deleteComponentLayers } from '../actions';
import { C_LAYER_DEFAULT, C_LAYER_MAX } from '../constants';
import { generateCLayerId } from '../utils/generateCLayerId';

interface Props {
  data: ComponentLayer;
  sceneId: GeneralID;
}

function ComponentLayerItem({ data, sceneId }: Props) {
  const { id, locked, visible, icon, data: featData } = data;

  const handleClick = () => {
    $editingComponentLayerApi.set({
      sceneId,
      layer: {
        ...data,
        animation: data.animation ?? C_LAYER_DEFAULT.animation,
        enterAnimation: data.enterAnimation ?? C_LAYER_DEFAULT.enterAnimation,
        exitAnimation: data.exitAnimation ?? C_LAYER_DEFAULT.exitAnimation,
        hideInClearMode:
          data.hideInClearMode ?? C_LAYER_DEFAULT.hideInClearMode,
      },
    });
  };

  const handleDuplicateClick = () => {
    if (($curScene.getState()?.config.components?.length ?? 0) >= C_LAYER_MAX) {
      sendWarning('复制图层失败，当前场景图层数超过上限');
      return;
    }
    const newId = generateCLayerId();
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.config.components ??= [];
        const list = draft.config.components;
        const index = list.findIndex((x) => x.id === id);
        if (index > -1) {
          const newItem: ComponentLayer = {
            ...list[index],
            id: newId,
            layerControl: C_LAYER_DEFAULT.layerControl,
          };
          list.splice(index + 1, 0, newItem);
        }
        list.forEach((x, i) => {
          x.zIndex = i;
        });
      },
    });
  };

  const toggleValue = (key: 'locked' | 'visible', value: boolean) => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        const target = draft.config.components?.find((x) => x.id === id);
        if (target) {
          target[key] = value;
        }
      },
    });
  };

  return (
    <div className="relative">
      {data.locked && <IconLocked className="absolute top-2 left-2 scale-75" />}
      <div
        className="mb-10 flex h-35 w-full cursor-pointer items-center rounded bg-black-#34 px-10"
        style={{ border: visible ? '' : '1px dashed gray' }}
        onClick={handleClick}
      >
        <Image
          className="h-16 w-16 flex-shrink-0"
          src={icon.mediaUrl}
          type={icon.mediaType === 0 ? ImageType.sysMedia : ImageType.media}
          size={16}
        />
        <div className="mx-10 truncate text-base text-white" title={data.title}>
          {data.title}
        </div>
        <div className="ml-auto flex-shrink-0 text-sm text-black-gray">
          {getFeatName(featData.code)}
        </div>
        <TipWrapper text="复制" placement="bottom" className="ml-10" size="sm">
          <IconDuplicate
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicateClick();
            }}
          />
        </TipWrapper>
        <ListItemMoreButton
          items={
            [
              locked ? '解锁' : '锁定',
              visible ? '隐藏' : '显示',
              '删除',
            ] as const
          }
          onSelect={async (val) => {
            if (val === '解锁') {
              toggleValue('locked', false);
            } else if (val === '锁定') {
              toggleValue('locked', true);
            } else if (val === '隐藏') {
              toggleValue('visible', false);
            } else if (val === '显示') {
              toggleValue('visible', true);
            } else if (val === '删除') {
              deleteComponentLayers({ sceneId, ids: id });
            }
          }}
          className="ml-10"
        />
      </div>
    </div>
  );
}

export default SortableElement(ComponentLayerItem);
